// A cinematic 3D scene rendered behind the CROSSFIRE title.
// - Slow-panning camera dolly
// - Exponential fog + layered light shafts to fake volumetric lighting
// - Two dark operator silhouettes with rifles
// - Broken cover and drifting embers
// Kept isolated so the intro can render this without touching menu/match scenes.
import { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

function OperatorSilhouette({ position = [0, 0, 0], rotation = [0, 0, 0], mirror = false, breatheOffset = 0 }) {
    const group = useRef();
    useFrame((state) => {
        if (!group.current) return;
        const t = state.clock.getElapsedTime() + breatheOffset;
        // Very subtle idle breathing
        group.current.position.y = position[1] + Math.sin(t * 1.1) * 0.015;
        group.current.rotation.y = rotation[1] + Math.sin(t * 0.35) * 0.02;
    });
    const dir = mirror ? -1 : 1;
    return (
        <group ref={group} position={position} rotation={rotation}>
            {/* Torso */}
            <mesh castShadow position={[0, 1.1, 0]}>
                <capsuleGeometry args={[0.36, 0.9, 4, 12]} />
                <meshStandardMaterial color="#050505" roughness={0.85} metalness={0.15} />
            </mesh>
            {/* Head */}
            <mesh castShadow position={[0, 1.95, 0]}>
                <sphereGeometry args={[0.24, 16, 16]} />
                <meshStandardMaterial color="#050505" roughness={0.75} metalness={0.2} />
            </mesh>
            {/* Helmet cap */}
            <mesh castShadow position={[0, 2.06, 0]}>
                <sphereGeometry args={[0.27, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
                <meshStandardMaterial color="#030303" roughness={0.6} metalness={0.4} />
            </mesh>
            {/* NVG visor slit — very faint */}
            <mesh position={[0, 1.98, 0.24 * dir]}>
                <boxGeometry args={[0.32, 0.05, 0.02]} />
                <meshStandardMaterial
                    color="#ffb877"
                    emissive="#ffb877"
                    emissiveIntensity={0.6}
                    metalness={0.3}
                    roughness={0.4}
                />
            </mesh>
            {/* Shoulders */}
            <mesh castShadow position={[0, 1.55, 0]}>
                <boxGeometry args={[0.9, 0.14, 0.4]} />
                <meshStandardMaterial color="#040404" roughness={0.7} metalness={0.3} />
            </mesh>

            {/* Rifle held forward, angled slightly across chest */}
            <group position={[0.15 * dir, 1.15, 0.3 * dir]} rotation={[0, (Math.PI / 8) * dir, 0]}>
                {/* Body */}
                <mesh castShadow>
                    <boxGeometry args={[0.06, 0.09, 0.7]} />
                    <meshStandardMaterial color="#0a0a0a" metalness={0.65} roughness={0.35} />
                </mesh>
                {/* Barrel */}
                <mesh castShadow position={[0, 0.02, 0.45]}>
                    <cylinderGeometry args={[0.02, 0.02, 0.5, 8]} rotation={[Math.PI / 2, 0, 0]} />
                    <meshStandardMaterial color="#050505" metalness={0.9} roughness={0.3} />
                </mesh>
                {/* Optic */}
                <mesh position={[0, 0.09, 0.15]}>
                    <boxGeometry args={[0.05, 0.06, 0.16]} />
                    <meshStandardMaterial color="#080808" metalness={0.4} roughness={0.4} />
                </mesh>
                {/* Magazine */}
                <mesh position={[0, -0.12, -0.05]}>
                    <boxGeometry args={[0.05, 0.16, 0.08]} />
                    <meshStandardMaterial color="#0a0a0a" metalness={0.5} roughness={0.5} />
                </mesh>
            </group>
        </group>
    );
}

function BrokenCover({ position, size = [1.4, 1.2, 1] }) {
    return (
        <mesh position={position} castShadow receiveShadow>
            <boxGeometry args={size} />
            <meshStandardMaterial color="#141210" roughness={0.9} metalness={0.15} />
        </mesh>
    );
}

function GodRay({ position, size = [0.6, 8, 0.6], color = "#ffb877" }) {
    // Faked light shaft: a very thin, faintly emissive cone using a plane
    return (
        <mesh position={position} rotation={[0, 0, Math.PI / 24]}>
            <coneGeometry args={[size[0], size[1], 6, 1, true]} />
            <meshBasicMaterial
                color={color}
                transparent
                opacity={0.06}
                depthWrite={false}
                side={THREE.DoubleSide}
            />
        </mesh>
    );
}

function Embers() {
    // Simple animated points that drift up like embers
    const ref = useRef();
    const count = 80;
    const positions = useRef(
        new Float32Array(
            Array.from({ length: count * 3 }, () => (Math.random() - 0.5) * 16)
        )
    );

    useFrame((state, dt) => {
        if (!ref.current) return;
        const arr = ref.current.geometry.attributes.position.array;
        for (let i = 0; i < count; i++) {
            arr[i * 3 + 1] += dt * (0.15 + (i % 5) * 0.03);
            if (arr[i * 3 + 1] > 6) {
                arr[i * 3 + 1] = -1;
                arr[i * 3] = (Math.random() - 0.5) * 16;
                arr[i * 3 + 2] = (Math.random() - 0.5) * 8 - 2;
            }
        }
        ref.current.geometry.attributes.position.needsUpdate = true;
    });

    return (
        <points ref={ref}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    array={positions.current}
                    count={count}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                color="#ffb877"
                size={0.03}
                sizeAttenuation
                transparent
                opacity={0.7}
                depthWrite={false}
            />
        </points>
    );
}

function CameraDolly({ active }) {
    // Slow forward push during the intro. Camera looks slightly downward
    // so the operators & title sit in a cinematic frame.
    const startZ = 8;
    const endZ = 4.5;
    useFrame((state) => {
        if (!active) return;
        const t = Math.min(1, state.clock.getElapsedTime() / 6);
        const eased = 1 - Math.pow(1 - t, 2.4);
        state.camera.position.set(
            Math.sin(state.clock.getElapsedTime() * 0.05) * 0.25,
            1.6 + Math.sin(state.clock.getElapsedTime() * 0.15) * 0.05,
            startZ - (startZ - endZ) * eased
        );
        state.camera.lookAt(0, 1.35, 0);
    });
    return null;
}

function Scene({ active }) {
    return (
        <>
            <fog attach="fog" args={["#050505", 3, 22]} />
            <ambientLight intensity={0.14} color="#7a8ba0" />

            {/* Key overhead light (amber, off-axis) */}
            <spotLight
                position={[-3, 8, 4]}
                angle={0.45}
                penumbra={0.7}
                intensity={1.6}
                color="#ffb877"
                distance={22}
                castShadow
            />
            {/* Cool rim from behind */}
            <spotLight
                position={[3, 6, -6]}
                angle={0.55}
                penumbra={0.7}
                intensity={0.7}
                color="#6b8ba7"
                distance={20}
            />

            {/* Ground */}
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0, 0]}
                receiveShadow
            >
                <planeGeometry args={[40, 40]} />
                <meshStandardMaterial color="#0a0908" roughness={0.95} metalness={0.15} />
            </mesh>

            {/* Broken cover on the floor */}
            <BrokenCover position={[-4.5, 0.6, -1]} size={[1.4, 1.2, 1]} />
            <BrokenCover position={[4.5, 0.5, -1.5]} size={[1.2, 1.0, 0.9]} />
            <BrokenCover position={[-2.5, 0.3, -3]} size={[1, 0.6, 0.8]} />
            <BrokenCover position={[2.5, 0.35, -3.2]} size={[1, 0.7, 0.8]} />
            <BrokenCover position={[0, 0.2, -5]} size={[1.6, 0.4, 0.6]} />

            {/* Distant wall silhouette */}
            <mesh position={[0, 2, -10]} receiveShadow>
                <boxGeometry args={[24, 4, 0.2]} />
                <meshStandardMaterial color="#0a0908" roughness={0.9} />
            </mesh>

            {/* Operators (foreground left/right, cropped by frame) */}
            <OperatorSilhouette position={[-3.4, 0, 1]} rotation={[0, Math.PI / 6, 0]} breatheOffset={0} />
            <OperatorSilhouette
                position={[3.4, 0, 1]}
                rotation={[0, -Math.PI / 6, 0]}
                mirror
                breatheOffset={1.5}
            />

            {/* Fake volumetric light shafts */}
            <GodRay position={[-3, 4, 0]} size={[1.2, 8, 1.2]} color="#ffb877" />
            <GodRay position={[2.5, 4, -1]} size={[0.8, 8, 0.8]} color="#ffcc99" />
            <GodRay position={[0, 4, -3]} size={[1.6, 8, 1.6]} color="#ffb877" />

            {/* Drifting embers */}
            <Embers />

            <CameraDolly active={active} />
        </>
    );
}

export default function IntroCinematic({ active = true }) {
    return (
        <div
            className="absolute inset-0"
            style={{
                opacity: active ? 1 : 0,
                transition: "opacity 900ms ease",
                pointerEvents: "none",
            }}
        >
            <Canvas
                shadows
                camera={{ position: [0, 1.6, 8], fov: 36 }}
                gl={{
                    antialias: true,
                    powerPreference: "high-performance",
                    toneMapping: THREE.ACESFilmicToneMapping,
                    toneMappingExposure: 1.1,
                }}
                dpr={[1, 1.5]}
                frameloop="always"
            >
                <color attach="background" args={["#040404"]} />
                <Suspense fallback={null}>
                    <Scene active={active} />
                </Suspense>
            </Canvas>

            {/* Overlay tint + heavy vignette so 2D title stays readable */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 55%, transparent 20%, rgba(0,0,0,0.55) 65%, rgba(0,0,0,0.9) 100%)",
                }}
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 25%, transparent 55%, rgba(0,0,0,0.9) 100%)",
                }}
            />
        </div>
    );
}
